const { MinimogThemeStyles, MinimogThemeScripts } = window;

class QuickView {
  constructor() {
    this.modal = new MinimogTheme.Modal();
    this.isOpen = false;
    this.isLoading = false;
    this.requestId = 0;
    this.activeTarget = null;

    // Event delegation for quick view buttons across the store
    addEventDelegate({
      selector: ".m-product-quickview-button",
      handler: (e, target) => {
        e.preventDefault();
        const productHandle = target.dataset.productHandle;
        if (!productHandle) return;

        // Clean up any previously loading target
        if (this.activeTarget && this.activeTarget !== target) {
          this.toggleLoading(this.activeTarget, false);
        }

        this.activeTarget = target;
        this.toggleLoading(target, true);
        this.loadProductQuickView(productHandle, target);
      },
    });

    // Subscribe to cart update events to close quick view after successful ATC
    window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
      if (this.modal && this.isOpen) {
        this.close();
      }
    });

    // Handle ESC key press to close modal
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });
  }

  // Build root-safe URL
  getProductUrl(productHandle) {
    const rootUrl = (window.MinimogSettings?.base_url || window.Shopify?.routes?.root || "/").replace(/\/$/, "");
    return `${rootUrl}/products/${productHandle}`;
  }

  // Load product quick view HTML and assets
  loadProductQuickView(productHandle, triggerTarget) {
    const currentRequestId = ++this.requestId;
    this.isLoading = true;

    // If modal is currently open with another product, close and reset inner content immediately
    if (this.isOpen) {
      if (this.modal && this.modal.modalContentInner) {
        this.modal.modalContentInner.innerHTML = "";
      }
      this.isOpen = false;
    }

    const productUrl = this.getProductUrl(productHandle);

    this.loadAssets(
      [MinimogThemeStyles.product, MinimogThemeStyles.productInventory].filter(Boolean),
      "quick-view-assets"
    )
      .then(() => fetchSection("product-quickview", { url: productUrl }))
      .then((html) => {
        // Discard result if a newer request was made in the meantime
        if (this.requestId !== currentRequestId) return;

        this.modalContent = html.querySelector(".m-product-quickview");
        if (!this.modalContent) {
          throw new Error("Could not find .m-product-quickview element in fetched HTML");
        }

        const firstModel = html.querySelector("product-model");
        this.mediaGallery = this.modalContent.querySelector("media-gallery");

        const scriptsToLoad = [
          MinimogThemeScripts.productMedia,
          MinimogThemeScripts.variantsPicker,
          MinimogThemeScripts.productInventory,
        ].filter(Boolean);

        return this.loadAssets(scriptsToLoad, "variants-picker").then(() => {
          if (this.requestId !== currentRequestId) return;

          const colorScheme = this.modalContent.dataset.colorScheme || "";

          // Clear any stale content from previous modal instances
          if (this.modal.modalContentInner) {
            this.modal.modalContentInner.innerHTML = "";
          }

          this.modal.appendChild(this.modalContent);
          this.modal.setWidth("960px");
          this.modal.open();
          if (colorScheme) {
            this.modal.setSizes(`m-gradient ${colorScheme}`);
          }

          this.toggleLoading(triggerTarget, false);
          this.isOpen = true;
          this.isLoading = false;
          this.activeTarget = null;

          if (firstModel && MinimogThemeScripts.productModel) {
            return this.loadAssets(
              [
                MinimogThemeScripts.productModel,
                "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css",
              ],
              "product-model-assets"
            );
          }
        });
      })
      .then(() => {
        if (this.requestId !== currentRequestId) return;

        document.dispatchEvent(
          new CustomEvent("quick-view:loaded", {
            detail: { productUrl: productHandle },
          })
        );
      })
      .catch((error) => {
        console.error("Error loading product quick view:", error);
        if (this.requestId === currentRequestId) {
          this.toggleLoading(triggerTarget, false);
          this.isLoading = false;
          this.activeTarget = null;
        }
      });
  }

  // Load assets helper function
  loadAssets(assets, key) {
    return new Promise((resolve, reject) => {
      if (!assets || assets.length === 0) {
        resolve();
        return;
      }
      loadAssetsNew(assets, key, resolve, reject);
    });
  }

  // Close the quick view modal
  close(e) {
    if (this.modal) {
      this.modal.close(e);
      if (this.modal.modalContentInner) {
        // Clear inner HTML after closing to prevent memory leaks and stale elements
        setTimeout(() => {
          if (!this.isOpen && this.modal && this.modal.modalContentInner) {
            this.modal.modalContentInner.innerHTML = "";
          }
        }, 300);
      }
    }
    this.isOpen = false;
    if (this.activeTarget) {
      this.toggleLoading(this.activeTarget, false);
      this.activeTarget = null;
    }
  }

  // Toggle loading spinner on a target element
  toggleLoading(target, isLoading) {
    if (target && target.classList) {
      target.classList[isLoading ? "add" : "remove"]("m-spinner-loading");
    }
  }
}

// Initialize the QuickView instance
MinimogTheme.ProductQuickView = new QuickView();
